import { FieldChange, ZuulAuditEvent, EventComparison, ParsedUser } from './types';
import * as jsondiffpatch from 'jsondiffpatch/with-text-diffs';

/**
 * Default blacklist of field paths to exclude from change comparison.
 * These fields often contain redundant nested data that creates noise in the diff.
 *
 * Examples:
 * - "offerQuestion.offer" - When editing an offer question, the entire offer is included but shouldn't be compared
 *
 * Usage: Pass this to computeEventComparisons or extend it with additional paths as needed.
 */
export const DEFAULT_COMPARISON_BLACKLIST: string[] = [
  'offerQuestion.offer',
  'offer.content',
  'offerTriggers[].offerQuestionAnswer',
  'offer.unNormalizedQuestionAnswers',
  'offer.offerActions[].answer',
  'offer.zuulUser',
  'offer.criteria.lastPublished',
  'offer.criteria.updatedAt',
  'offerAction.lastEdited',
  'offerAction.zuulUser',
  'container.zuulUser',
  'layout.zuulUser',
  'zuulUser',
  'lastEdited',
  'lastPublished',
  'offersSequences[].offerBlock'
];

/**
 * Parse the user field from a ZuulAuditEvent
 */
export const parseUser = (userString: string): ParsedUser => {
  try {
    return JSON.parse(userString);
  } catch {
    return { name: userString };
  }
};

/**
 * Get display name from parsed user
 */
export const getUserDisplayName = (userString: string): string => {
  const user = parseUser(userString);
  return user.name || user.email || userString || 'Unknown User';
};

/**
 * Parse request data from event
 */
export const parseRequestData = (event: ZuulAuditEvent): Record<string, unknown> | null => {
  if (!event.request?.data) return null;
  try {
    return JSON.parse(event.request.data);
  } catch {
    return null;
  }
};

const diffpatcher = jsondiffpatch.create({
  // Use object hash for better array item matching
  objectHash: (obj: unknown, index?: number) => {
    if (typeof obj === 'object' && obj !== null) {
      const record = obj as Record<string, unknown>;

      // Primary identifiers - use these if available
      if ('id' in record && record.id != null) return `id:${String(record.id)}`;
      if ('_id' in record && record._id != null) return `_id:${String(record._id)}`;

      // Offer sequences - use offer ID + orderBy for unique identification
      if ('offersSequence' in record || ('offer' in record && 'orderBy' in record)) {
        const offerId = (record.offer as Record<string, unknown>)?.id ??
          (record.offersSequence as Record<string, any>)?.offer?.id;
        const orderBy = record.orderBy;
        if (offerId != null) return `offerSeq:${offerId}:${orderBy ?? index}`;
      }

      // Offer triggers - identify by trigger type + question answer or action
      if ('triggerType' in record) {
        const triggerTypeId = (record.triggerType as Record<string, unknown>)?.id;
        const questionAnswerId = (record.offerQuestionAnswer as Record<string, unknown>)?.id;
        const questionAnswerText = (record.offerQuestionAnswer as Record<string, unknown>)?.answer;
        if (questionAnswerId != null) return `trigger:${triggerTypeId}:qa:${questionAnswerId}`;
        if (questionAnswerText != null) return `trigger:${triggerTypeId}:answer:${questionAnswerText}`;
        return `trigger:${triggerTypeId}:${index}`;
      }

      // Offer action triggers - identify by action ID and type
      if ('offerAction' in record && 'offerActionType' in record) {
        const actionId = (record.offerAction as Record<string, unknown>)?.id;
        const actionType = record.offerActionType;
        if (actionId != null) return `actionTrigger:${actionId}:${actionType}`;
      }

      // Offer question answers - identify by answer text or id
      if ('answer' in record && 'offerQuestion' in record) {
        if ('id' in record && record.id != null) return `qa:${record.id}`;
        return `qa:answer:${record.answer}:order:${record.orderNumber ?? index}`;
      }

      // Question answers (unnormalized) - identify by text and order
      if ('text' in record && ('orderNumber' in record || 'question_actions' in record)) {
        const text = record.text;
        const orderNumber = record.orderNumber ?? index;
        return `answerText:${text}:${orderNumber}`;
      }

      // Generic fallback for objects with name + type combination
      if ('name' in record && 'offerActionType' in record) {
        return `action:${record.name}:${record.offerActionType}`;
      }

      // For objects without clear identifiers, use a content-based hash
      // but include index to detect moves
      return `idx:${index}:${JSON.stringify(obj)}`;
    }
    return JSON.stringify(obj);
  },
  arrays: {
    detectMove: true,
    includeValueOnMove: false,
  },
});

/**
 * Normalize a field path by removing numeric indices from array brackets.
 * Example: "offer[1].test" becomes "offer[].test"
 */
const normalizeFieldPath = (fieldPath: string): string => {
  return fieldPath.replace(/\[\d+\]/g, '[]');
};

/**
 * Check if a field path should be excluded based on the blacklist.
 * A path is blacklisted if it matches or starts with any blacklisted pattern.
 *
 * @param fieldPath - The current field path (e.g., "offerQuestion.offer.name")
 * @param blacklist - Array of paths to exclude (e.g., ["offerQuestion.offer"])
 * @returns true if the path should be skipped
 */
const isPathBlacklisted = (fieldPath: string, blacklist: string[]): boolean => {
  if (!blacklist || blacklist.length === 0) return false;

  const normalizedPath = normalizeFieldPath(fieldPath);

  return blacklist.some(blacklistedPath => {
    // Exact match
    if (normalizedPath === blacklistedPath) return true;
    // Path starts with blacklisted path followed by a dot or bracket (for nested properties)
    if (normalizedPath.startsWith(`${blacklistedPath}.`) || normalizedPath.startsWith(`${blacklistedPath}[`)) return true;
    return false;
  });
};

/**
 * Deep comparison of two objects using jsondiffpatch, returning field changes with dot notation paths
 *
 * @param oldObj - The previous state of the object
 * @param newObj - The current state of the object
 * @param blacklist - Optional array of field paths to exclude from comparison (e.g., ["offerQuestion.offer"])
 */
export const compareObjects = (
  oldObj: Record<string, unknown> | null,
  newObj: Record<string, unknown> | null,
  blacklist: string[] = []
): FieldChange[] => {
  const changes: FieldChange[] = [];

  // Handle null cases
  if (oldObj === null && newObj === null) return changes;

  if (oldObj === null && newObj !== null) {
    // Everything in newObj is added - create a single "added" entry for the whole object
    changes.push({
      fieldName: '(root)',
      oldValue: undefined,
      newValue: newObj,
      changeType: 'added',
    });
    return changes;
  }

  if (oldObj !== null && newObj === null) {
    // Everything in oldObj is removed - create a single "removed" entry
    changes.push({
      fieldName: '(root)',
      oldValue: oldObj,
      newValue: undefined,
      changeType: 'removed',
    });
    return changes;
  }

  // Both objects exist - use jsondiffpatch to compare them
  const delta = diffpatcher.diff(oldObj, newObj);

  if (!delta) {
    // No differences
    return changes;
  }

  // Parse the delta to extract changes
  parseDelta(delta, '', oldObj!, newObj!, changes, blacklist);

  return changes;
};

/**
 * Parse jsondiffpatch delta recursively and extract FieldChange entries
 *
 * Delta format:
 * - Added: [newValue]
 * - Removed: [oldValue, 0, 0]
 * - Modified: [oldValue, newValue]
 * - Text diff: [diffString, 0, 2] (we treat as modified)
 * - Nested object: { key: delta }
 * - Array: { _t: 'a', index: delta, _index: delta } where _index means removed
 */
const parseDelta = (
  delta: jsondiffpatch.Delta,
  path: string,
  oldObj: Record<string, unknown>,
  newObj: Record<string, unknown>,
  changes: FieldChange[],
  blacklist: string[] = []
): void => {
  if (!delta || typeof delta !== 'object') return;

  // Check if this is an array delta
  if ('_t' in delta && delta._t === 'a') {
    parseArrayDelta(delta, path, oldObj, newObj, changes, blacklist);
    return;
  }

  // Process each key in the delta
  for (const key of Object.keys(delta)) {
    if (key === '_t') continue; // Skip array type marker

    const fieldPath = path ? `${path}.${key}` : key;

    // Skip blacklisted paths
    if (isPathBlacklisted(fieldPath, blacklist)) continue;

    const deltaValue = (delta as Record<string, unknown>)[key];

    if (Array.isArray(deltaValue)) {
      // This is a leaf change
      if (deltaValue.length === 1) {
        // Added: [newValue]

        if(isNullishOrEmpty(deltaValue[0]))
          continue;

        changes.push({
          fieldName: fieldPath,
          oldValue: undefined,
          newValue: deltaValue[0],
          changeType: 'added',
        });
      } else if (deltaValue.length === 3 && deltaValue[2] === 0) {
        // Removed: [oldValue, 0, 0]

        if(isNullishOrEmpty(deltaValue[0]))
          continue;

        changes.push({
          fieldName: fieldPath,
          oldValue: deltaValue[0],
          newValue: undefined,
          changeType: 'removed',
        });
      } else if (deltaValue.length === 3 && deltaValue[2] === 2) {
        // Text diff: [diffString, 0, 2] - treat as modified
        const oldValue = getNestedValue(oldObj, fieldPath);
        const newValue = getNestedValue(newObj, fieldPath);

        if(shouldSkipNullishChange(oldValue, newValue))
          continue;

        changes.push({
          fieldName: fieldPath,
          oldValue,
          newValue,
          changeType: 'modified',
        });
      } else if (deltaValue.length === 2) {
        // Modified: [oldValue, newValue]

        if(shouldSkipNullishChange(deltaValue[0], deltaValue[1]))
          continue;

        changes.push({
          fieldName: fieldPath,
          oldValue: deltaValue[0],
          newValue: deltaValue[1],
          changeType: 'modified',
        });
      }
    } else if (typeof deltaValue === 'object' && deltaValue !== null) {
      // Nested object - recurse
      const oldNested = (oldObj?.[key] ?? {}) as Record<string, unknown>;
      const newNested = (newObj?.[key] ?? {}) as Record<string, unknown>;
      parseDelta(deltaValue as jsondiffpatch.Delta, fieldPath, oldNested, newNested, changes, blacklist);
    }
  }
};

/**
 * Check if a value is considered "nullish" or empty.
 * Returns true for: null, undefined, empty string, empty array, empty object
 */
const isNullishOrEmpty = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object' && Object.keys(value).length === 0) return true;
  return false;
};

/**
 * Check if a change between two values should be skipped because both are nullish/empty.
 * This filters out noise like: null → [], undefined → "", {} → null, etc.
 */
const shouldSkipNullishChange = (oldValue: unknown, newValue: unknown): boolean => {
  return isNullishOrEmpty(oldValue) && isNullishOrEmpty(newValue);
};

/**
 * Parse array-specific delta from jsondiffpatch
 */
const parseArrayDelta = (
  delta: jsondiffpatch.Delta,
  path: string,
  oldObj: Record<string, unknown>,
  newObj: Record<string, unknown>,
  changes: FieldChange[],
  blacklist: string[] = []
): void => {
  // Skip if the array path itself is blacklisted
  if (path && isPathBlacklisted(path, blacklist)) return;

  const addedItems: unknown[] = [];
  const removedItems: unknown[] = [];

  // Get the actual arrays
  const oldArray = (path ? getNestedValue(oldObj, path) : oldObj) as unknown[];
  const newArray = (path ? getNestedValue(newObj, path) : newObj) as unknown[];

  if (!delta || typeof delta !== 'object') return;

  for (const key of Object.keys(delta)) {
    if (key === '_t') continue;

    const deltaValue = (delta as Record<string, unknown>)[key];

    if (key.startsWith('_')) {
      // Removed item: key is _index (e.g., '_0', '_1')
      const index = parseInt(key.substring(1), 10);
      if (Array.isArray(deltaValue) && deltaValue.length === 3 && deltaValue[2] === 0) {
        removedItems.push(deltaValue[0]);
      } else if (Array.isArray(oldArray) && index < oldArray.length) {
        removedItems.push(oldArray[index]);
      }
    } else {
      // Added or modified item
      const index = parseInt(key, 10);
      const itemPath = `${path}[${index}]`;

      // Skip if this array item path is blacklisted
      if (isPathBlacklisted(itemPath, blacklist)) continue;

      if (Array.isArray(deltaValue)) {
        if (deltaValue.length === 1) {
          // Added item
          addedItems.push(deltaValue[0]);
        } else if (deltaValue.length === 2) {
          // Modified item in place
          changes.push({
            fieldName: itemPath,
            oldValue: deltaValue[0],
            newValue: deltaValue[1],
            changeType: 'modified',
          });
        } else if (deltaValue.length === 3 && deltaValue[2] === 0) {
          // This shouldn't happen for non-underscore keys, but handle it
          removedItems.push(deltaValue[0]);
        }
      } else if (typeof deltaValue === 'object' && deltaValue !== null) {
        // Nested change within an array item
        const oldItem = (Array.isArray(oldArray) ? oldArray[index] : {}) as Record<string, unknown>;
        const newItem = (Array.isArray(newArray) ? newArray[index] : {}) as Record<string, unknown>;
        parseDelta(deltaValue as jsondiffpatch.Delta, itemPath, oldItem, newItem, changes, blacklist);
      }
    }
  }

  // Add collected array changes
  if (addedItems.length > 0) {
    changes.push({
      fieldName: path,
      oldValue: undefined,
      newValue: addedItems,
      changeType: 'added',
    });
  }

  if (removedItems.length > 0) {
    changes.push({
      fieldName: path,
      oldValue: removedItems,
      newValue: undefined,
      changeType: 'removed',
    });
  }
};

/**
 * Get a nested value from an object using dot notation path
 */
const getNestedValue = (obj: Record<string, unknown>, path: string): unknown => {
  const parts = path.replace(/\[(\d+)\]/g, '.$1').split('.');
  let current: unknown = obj;

  for (const part of parts) {
    if (current === null || current === undefined) return undefined;
    if (typeof current === 'object') {
      current = (current as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }

  return current;
};

/**
 * Format a value for display
 */
export const formatValue = (value: unknown): string => {
  if (value === undefined || value === null) return '(empty)';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

/**
 * Generate natural language description for a field change
 * Returns an object with parts that can be styled differently
 */
export interface ChangeDescriptionParts {
  userName: string;
  action: string;
  fieldName: string;
  oldValue?: string;
  newValue?: string;
  suffix?: string;
  changeType: 'added' | 'removed' | 'modified';
}

export const generateChangeDescriptionParts = (change: FieldChange, userName: string): ChangeDescriptionParts => {
  const fieldName = formatFieldName(change.fieldName);

  switch (change.changeType) {
    case 'added':
      if (Array.isArray(change.newValue)) {
        const items = change.newValue.map(v => formatValue(v)).join(', ');
        return {
          userName,
          action: 'added',
          fieldName,
          newValue: items,
          suffix: ` to`,
          changeType: 'added',
        };
      }
      return {
        userName,
        action: 'added',
        fieldName,
        newValue: formatValue(change.newValue),
        suffix: ' to',
        changeType: 'added',
      };

    case 'removed':
      if (Array.isArray(change.oldValue)) {
        const items = change.oldValue.map(v => formatValue(v)).join(', ');
        return {
          userName,
          action: 'removed',
          fieldName,
          oldValue: items,
          suffix: ' from',
          changeType: 'removed',
        };
      }
      return {
        userName,
        action: 'removed',
        fieldName,
        oldValue: formatValue(change.oldValue),
        suffix: ' (was',
        changeType: 'removed',
      };

    case 'modified':
      return {
        userName,
        action: 'changed',
        fieldName,
        oldValue: formatValue(change.oldValue),
        newValue: formatValue(change.newValue),
        changeType: 'modified',
      };

    default:
      return {
        userName,
        action: 'modified',
        fieldName,
        changeType: 'modified',
      };
  }
};


/**
 * Generate natural language description for a field change (plain string version)
 */
export const generateChangeDescription = (change: FieldChange, userName: string): string => {
  const fieldName = formatFieldName(change.fieldName);

  switch (change.changeType) {
    case 'added':
      if (Array.isArray(change.newValue)) {
        const items = change.newValue.map(v => formatValue(v)).join(', ');
        return `${userName} added ${items} to ${fieldName}`;
      }
      return `${userName} added ${fieldName} with value "${formatValue(change.newValue)}"`;

    case 'removed':
      if (Array.isArray(change.oldValue)) {
        const items = change.oldValue.map(v => formatValue(v)).join(', ');
        return `${userName} removed ${items} from ${fieldName}`;
      }
      return `${userName} removed ${fieldName} (was "${formatValue(change.oldValue)}")`;

    case 'modified':
      return `${userName} changed ${fieldName} from "${formatValue(change.oldValue)}" to "${formatValue(change.newValue)}"`;

    default:
      return `${userName} modified ${fieldName}`;
  }
};

/**
 * Format field name for display (convert camelCase/dot notation to readable text)
 */
const formatFieldName = (fieldName: string): string => {
  return fieldName
    .split('.')
    .map(part =>
      part
        .replace(/\[(\d+)\]/g, (match, n) => ` #${parseInt(n) + 1}`)
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim(),
    )
    .join(' > ');
};

/**
 * Extract resource ID from URL (for matching POST to subsequent PUT)
 */
export const extractResourceFromUrl = (url: string): { basePath: string; id: string | null } => {
  // Remove query params
  const urlWithoutQuery = url.split('?')[0];
  // Match pattern like /api/resource/123 or /api/resource
  const match = urlWithoutQuery?.match(/^(.+?)(?:\/(\d+))?$/);

  if (match) {
    return {
      basePath: match[1] || '',
      id: match[2] || null,
    };
  }

  return { basePath: url, id: null };
};

/**
 * Match events and compute comparisons
 * Groups events by URL and compares consecutive events
 * For POST requests, tries to match with subsequent PUT requests
 *
 * @param events - Array of audit events to compare
 * @param blacklist - Optional array of field paths to exclude from comparison (e.g., ["offerQuestion.offer"])
 * @param activeUrlFilters - Optional array of URL patterns to include in the comparison (e.g., ["/api/offer/123", "/api/offer"])
 */
export const computeEventComparisons = (
  events: ZuulAuditEvent[],
  blacklist: string[] = [],
  activeUrlFilters: string[] = [],
): EventComparison[] => {
  if (!events || events.length === 0) return [];

  // Sort events by timestamp (newest first for display, but we need oldest first for comparison)
  const sortedEvents = [...events].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  // Group events by URL pattern
  const eventsByUrl = new Map<string, ZuulAuditEvent[]>();
  const postEvents = new Map<string, ZuulAuditEvent>(); // Track POST events by base path

  for (const event of sortedEvents) {
    const url = event.request?.url || '';
    const method = event.request?.method?.toUpperCase();
    const { basePath } = extractResourceFromUrl(url);

    if (method === 'POST') {
      // Store POST events by base path for later matching
      postEvents.set(basePath, event);
    }

    // Group by full URL for PUT/PATCH/DELETE, or by base path for comparison
    const groupKey = url;

    if (!eventsByUrl.has(groupKey)) {
      eventsByUrl.set(groupKey, []);
    }
    eventsByUrl.get(groupKey)!.push(event);
  }

  // Build comparisons
  const comparisons: EventComparison[] = [];

  // Process events in reverse order (newest first for display)
  const reversedEvents = [...sortedEvents].reverse();

  for (const event of reversedEvents) {
    const url = event.request?.url || '';
    const method = event.request?.method?.toUpperCase();
    const { basePath, id } = extractResourceFromUrl(url);
    const userName = getUserDisplayName(event.user);

    if (activeUrlFilters.length > 0 && !activeUrlFilters.includes(url)) {
      continue;
    }

    let previousEvent: ZuulAuditEvent | null = null;

    if (method === 'POST') {
      // POST creates a new resource - no previous event
      previousEvent = null;
    } else {
      // Find the most recent previous event for the same URL
      const urlEvents = eventsByUrl.get(url) || [];
      const eventIndex = urlEvents.findIndex(e => e.timestamp === event.timestamp);

      if (eventIndex > 0) {
        previousEvent = urlEvents[eventIndex - 1] || null;
      } else if (id) {
        // Check if there's a POST event for the base path (resource creation)
        const postEvent = postEvents.get(basePath);
        if (postEvent && new Date(postEvent.timestamp) < new Date(event.timestamp)) {
          previousEvent = postEvent;
        }
      }
    }

    // Compute changes
    const currentData = parseRequestData(event);
    const previousData = previousEvent ? parseRequestData(previousEvent) : null;
    const changes = compareObjects(previousData, currentData, blacklist);

    comparisons.push({
      currentEvent: event,
      previousEvent,
      changes,
      user: userName,
    });
  }

  return comparisons;
};
